import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'antd'
import { clsx } from 'clsx'

import B2becButton from '@/components/B2becButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { NotificationManager } from '@/components/Notification'
import { useCreateWishlist } from '@/pages/WishlistOverview/useFetchWishlist'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import BREAKPOINTS from '@/libs/constants/breakpoints'

import { NewWishlistNameInput } from '../NewWishlistNameInput'
import styles from './CreateWishlistModal.module.scss'

interface CreateWishlistModalProps {
  open: boolean
  onClose: () => void
  onSave?: (wishlistName: string) => void | Promise<void>
}

function CreateWishlistModal({ open, onClose, onSave }: CreateWishlistModalProps) {
  const isMountedRef = useRef(true)
  const { t } = useTranslation()
  const { mutate: createWishlist, isPending: isCreating } = useCreateWishlist()
  const [newWishlistName, setNewWishlistName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const isLargerThan992 = useMediaQuery(`(min-width:${BREAKPOINTS.lg}px)`)

  const canSave = newWishlistName.trim().length > 0

  const handleChangeNewWishlistName = (e) => {
    setNewWishlistName(e.target.value)
  }

  const handleSave = async () => {
    if (canSave) {
      if (onSave) {
        setIsSaving(true)
        try {
          await onSave(newWishlistName)
          if (isMountedRef.current) {
            onClose()
          }
        } catch (error) {
          if (isMountedRef.current) {
            NotificationManager.error({
              message: 'notification.error.createWishlist',
            })
            console.error('fail to create new wishlist', error)
          }
        } finally {
          if (isMountedRef.current) {
            setIsSaving(false)
          }
        }
        return
      }

      createWishlist(
        {
          wishlistName: newWishlistName,
        },
        {
          onSuccess: () => {
            NotificationManager.success({
              message: 'notification.success.createWishlist',
            })
            onClose()
          },
          onError: (error) => {
            NotificationManager.error({
              message: 'notification.error.createWishlist',
            })
            console.error('fail to create new wishlist', error)
          },
        },
      )
    }
  }

  const headerSection = (
    <div className={styles['modal__title-wrapper']}>
      <div className={styles['modal__title-left']}>
        {!isLargerThan992 && (
          <button
            className={clsx('button-as-link', styles['modal__close-button'])}
            type="button"
            onClick={onClose}
          >
            <ChevronLeftIcon height={24} width={24} />
          </button>
        )}
        <h1 className={styles.modal__title}>
          {t('wishlist.modal.newWishlist.title')}
        </h1>
      </div>
      {!isLargerThan992 && (
        <div className="modal__title-right">
          <B2becButton
            className={clsx('button-as-link', styles['modal__save-button'])}
            loading={isCreating || isSaving}
            onClick={handleSave}
          >
            {t('buttonTexts.save')}
          </B2becButton>
        </div>
      )}
    </div>
  )

  const newWishlistSection = (
    <>
      <span className={styles['new-wishlist__label']}>
        {t('wishlist.createNewWishlist')}
      </span>
      <NewWishlistNameInput
        className={styles['new-wishlist__input--no-margin']}
        placeholder={t('wishlist.placeholder.newWishlistName')}
        value={newWishlistName}
        onChange={handleChangeNewWishlistName}
      />
    </>
  )

  const CTASection = isLargerThan992
    ? (
      <div className={styles['modal__action-buttons']}>
        <B2becButton
          ghost
          className={styles['modal__action-button']}
          disabled={isCreating || isSaving}
          onClick={onClose}
        >
          {t('buttonTexts.cancel')}
        </B2becButton>
        <B2becButton
          className={styles['modal__action-button']}
          disabled={!canSave}
          loading={isCreating || isSaving}
          onClick={handleSave}
        >
          {t('buttonTexts.save')}
        </B2becButton>
      </div>
    )
    : null

  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  return (
    <Modal
      centered
      destroyOnHidden
      className={styles.modal}
      closable={false}
      footer={null}
      open={open}
      width={isLargerThan992 ? 540 : '100%'}
      onCancel={onClose}
    >
      {headerSection}
      {!isLargerThan992 && <div className={styles['break-line']} />}
      {isLargerThan992 && (
        <p className={styles.modal__description}>
          {t('wishlist.modal.newWishlist.description')}
        </p>
      )}
      {newWishlistSection}
      {isLargerThan992 && <div className={styles['break-line']} />}
      {CTASection}
    </Modal>
  )
}

export default CreateWishlistModal
